import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { ConnectorStatus, SimulatedData } from '../types';
import ConnectorPanel from './ConnectorPanel';
import MessageLogger from './MessageLogger';
import SessionManager from './SessionManager';
import { useWebSocket } from '../contexts/WebSocketContext';
import { v4 as uuidv4 } from 'uuid';

interface Connector {
  id: number;
  status: ConnectorStatus;
  currentPower: number;
  maxPower: number;
  current: number;
  chargePercentage: number;
  currentMeterReading?: string;
  activeSession?: {
    transactionId: string;
    idTag: string;
    startTime: Date;
    energyConsumed: number;
    sessionEnergyKwh?: string;
    chargingInterval?: NodeJS.Timeout;
  };
}

const ChargingStationSimulator: React.FC = () => {
  const {
    isConnected,
    stationId,
    setStationId,
    messages,
    connect,
    disconnect,
    sendHeartbeat,
    sendBootNotification,
    ocppClient,
    addOutgoingMessage
  } = useWebSocket();
  const [backendStatus, setBackendStatus] = useState<'UNKNOWN' | 'ACCEPTED' | 'REJECTED'>('UNKNOWN');
  const [stationName, setStationName] = useState('Demo Station Alpha');
  const [connectors, setConnectors] = useState<Connector[]>([
    { id: 1, status: ConnectorStatus.AVAILABLE, currentPower: 0, maxPower: 22000, current: 0, chargePercentage: 20 },
    { id: 2, status: ConnectorStatus.AVAILABLE, currentPower: 0, maxPower: 50000, current: 0, chargePercentage: 20 }
  ]);

  const [simulatedData, setSimulatedData] = useState<SimulatedData>({
    energyActiveImportRegister: 1000,
    powerActiveImport: 0,
    currentImport: 0,
    voltage: 230
  });
  const isFullyOperational = isConnected && backendStatus === 'ACCEPTED';
  const handleIncomingOCPP = (message: any) => {
    const { action, payload, messageTypeId } = message;

    // Handle RemoteStartTransaction from CCMS
    if (messageTypeId === 2 && action === "RemoteStartTransaction") {
      const connectorId = payload.connectorId;
      const transactionId = payload.transactionId;

      setConnectors(prev => prev.map(conn =>
        conn.id === connectorId
          ? {
            ...conn,
            status: ConnectorStatus.OCCUPIED,
            activeSession: {
              transactionId: transactionId,
              idTag: payload.idTag,
              startTime: new Date(),
              energyConsumed: 0, // Starts at 0
              meterStart: conn.currentMeterReading || 0
            }
          }
          : conn
      ));

      // Reply back to CCMS
      ocppClient?.sendCallResult(message.messageId, { status: "Accepted" });
    }
  };
  // useEffect(() => {
  //       const handleOcppCommand = (event: any) => {
  //         const { action, payload, messageId } = event.detail;
  //         if (!ocppClient) return;

  //         // 1. Immediate Handshake (Stops Java Timeout)
  //         ocppClient.sendCallResult(messageId, { status: "Accepted" });
  //         console.log(`>>> Sent handshake for: ${action}`);

  //         if (action === "RemoteStartTransaction") {
  //           const targetId = payload.connectorId;
  //           const idTag = payload.idTag;

  //           setConnectors(current => {
  //             const connector = current.find(c => c.id === targetId);
  //             if (connector) {
  //               toast.success("Mobile Start Request Received");

  //               // Auto-plug if car is Available
  //               if (connector.status === ConnectorStatus.AVAILABLE) {
  //                 plugInCar(targetId);
  //               }

  //               // Trigger the charging flow
  //               setTimeout(() => startCharging(targetId, idTag), 500);
  //             }
  //             return current;
  //           });
  //         }

  //         if (action === "RemoteStopTransaction") {
  //           setConnectors(current => {
  //             const connector = current.find(c => c.activeSession?.transactionId === payload.transactionId);
  //             if (connector) stopCharging(connector.id);
  //             return current;
  //           });
  //         }
  //       };

  //       window.addEventListener('ocpp-command', handleOcppCommand);
  //       return () => window.removeEventListener('ocpp-command', handleOcppCommand);
  //     }, [ocppClient]);


  const plugInCar = useCallback((connectorId: number) => {
    setConnectors(prev => prev.map(conn =>
      conn.id === connectorId ? { ...conn, status: ConnectorStatus.PREPARING } : conn
    ));

    if (ocppClient) {
      const messageId = ocppClient.sendStatusNotification(connectorId, ConnectorStatus.PREPARING);
      addOutgoingMessage('StatusNotification', {
        connectorId,
        connectorStatus: ConnectorStatus.PREPARING,
        timestamp: new Date().toISOString()
      }, messageId);
    }
  }, [ocppClient, addOutgoingMessage]);

  const handleConnect = () => {
    setBackendStatus('UNKNOWN'); // 🔄 Reset dot to Yellow / "Verifying"
    connect(); // 🔌 Open the WebSocket
  };

  // Also add a reset for disconnect to keep the UI clean
  const handleDisconnect = () => {
    setBackendStatus('UNKNOWN');
    disconnect();
  };


  // // Update simulated data based on active charging sessions
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setConnectors(current => current.map(conn => {
  //       if (conn.status === ConnectorStatus.OCCUPIED && conn.activeSession) {
  //         const newPercentage = Math.min(100, conn.chargePercentage + 0.1);

  //         // Calculate energy delivered for THIS session
  //         // (Total Station Energy - Total Station Energy when this session started)
  //         const sessionEnergyWh = simulatedData.energyActiveImportRegister - conn.activeSession.energyConsumed;

  //         return {
  //           ...conn,
  //           chargePercentage: parseFloat(newPercentage.toFixed(1)),
  //           // Keep power active
  //           currentPower: conn.id === 1 ? 22000 : 50000,
  //           current: conn.id === 1 ? 32 : 64
  //         };
  //       }
  //       return conn;
  //     }));

  //     // Update the global meter
  //     setSimulatedData(prev => {
  //       const totalPower = connectors.reduce((sum, conn) => sum + conn.currentPower, 0);
  //       return {
  //         ...prev,
  //         energyActiveImportRegister: prev.energyActiveImportRegister + (totalPower * 2) / 3600,
  //         powerActiveImport: totalPower,
  //       };
  //     });
  //   }, 2000);

  //   return () => clearInterval(interval);
  // }, [connectors, simulatedData.energyActiveImportRegister]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      connectors.forEach(conn => {
        if (conn.activeSession?.chargingInterval) {
          clearInterval(conn.activeSession.chargingInterval);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendHeartbeat = () => {
    if (backendStatus !== 'ACCEPTED') {
      toast.error("Cannot send heartbeat: Station not yet accepted by backend.");
      return;
    }
    sendHeartbeat();
  };

  const handleSendBootNotification = () => {
    sendBootNotification();
    toast.success('Boot notification sent');
  };

  const updateConnectorStatus = (connectorId: number, status: ConnectorStatus) => {
    setConnectors(prev => prev.map(conn =>
      conn.id === connectorId
        ? { ...conn, status }
        : conn
    ));

    if (ocppClient) {
      const messageId = ocppClient.sendStatusNotification(connectorId, status);
      addOutgoingMessage('StatusNotification', {
        connectorId,
        connectorStatus: status,
        timestamp: new Date().toISOString()
      }, messageId);
      toast.success(`Connector ${connectorId} status updated to ${status}`);
    }
  };

  const startCharging = useCallback((connectorId: number, remoteIdTag?: string, remoteTxId?: string) => {
    const idTag = remoteIdTag || `RFID_${Math.floor(Math.random() * 10000)}`;
    const transactionId = remoteTxId || uuidv4();

    setConnectors(prev => prev.map(conn =>
      conn.id === connectorId ? {
        ...conn,
        status: ConnectorStatus.OCCUPIED,
        currentPower: conn.id === 1 ? 22000 : 50000,
        current: conn.id === 1 ? 32 : 64,
        activeSession: {
          transactionId,
          idTag,
          startTime: new Date(),
          // STORE THE STARTING METER VALUE HERE
          energyConsumed: simulatedData.energyActiveImportRegister
        }
      } : conn
    ));

    if (ocppClient) {
      // Send the actual meter value to the backend so meterStart is not 0
      ocppClient.sendTransactionEvent('Started', transactionId, connectorId, undefined, idTag, simulatedData.energyActiveImportRegister);
    }
  }, [ocppClient, simulatedData.energyActiveImportRegister]);

  const stopCharging = useCallback((connectorId: number) => {
    setConnectors(prev => prev.map(conn => {
      if (conn.id === connectorId && conn.activeSession) {
        // 1. Capture final meter reading for the database
        const finalMeterValue = simulatedData.energyActiveImportRegister;

        if (ocppClient) {
          ocppClient.sendTransactionEvent(
            'Ended',
            conn.activeSession.transactionId,
            connectorId,
            'Remote', // Triggered by backend
            undefined,
            finalMeterValue
          );
          // Keep the status as OCCUPIED so the "Unplug" button stays visible
          ocppClient.sendStatusNotification(connectorId, ConnectorStatus.OCCUPIED);
        }

        return {
          ...conn,
          currentPower: 0, // Stop power flow
          current: 0,      // Stop current flow
          status: ConnectorStatus.OCCUPIED
        };
      }
      return conn;
    }));
  }, [ocppClient, simulatedData.energyActiveImportRegister]);

  const unplugCar = (connectorId: number) => {
    // 1. Capture current timestamp immediately
    const unplugTime = new Date().toISOString();

    setConnectors(prev => {
      // 2. Find the specific connector inside the state update to avoid the "Bad setState" warning
      const connector = prev.find(c => c.id === connectorId);

      // Safety check: if no connector or no session, just return state as is
      if (!connector) return prev;

      // 3. Clear local intervals if they exist
      if (connector.activeSession?.chargingInterval) {
        clearInterval(connector.activeSession.chargingInterval);
      }

      // 4. Send the OCPP messages OUTSIDE the return block but inside the event handler
      if (ocppClient && connector.activeSession) {
        // Trigger 'Ended' with 'EVDisconnected' to calculate idleTime in Java
        const transactionMessageId = ocppClient.sendTransactionEvent(
          'Ended',
          connector.activeSession.transactionId,
          connectorId,
          'EVDisconnected',
          undefined,
          simulatedData.energyActiveImportRegister
        );

        // Confirmation to the backend that hardware is now free
        ocppClient.sendStatusNotification(connectorId, ConnectorStatus.AVAILABLE);

        // Update the local message log
        addOutgoingMessage('TransactionEvent', {
          eventType: 'Ended',
          timestamp: unplugTime,
          triggerReason: 'EVDisconnected',
          transactionInfo: { transactionId: connector.activeSession.transactionId },
          evse: { id: connectorId }
        }, transactionMessageId);
      }

      // 5. Return the new state: everything reset to AVAILABLE and activeSession cleared
      return prev.map(conn =>
        conn.id === connectorId
          ? {
            ...conn,
            status: ConnectorStatus.AVAILABLE,
            currentPower: 0,
            current: 0,
            chargePercentage: 20,
            activeSession: undefined // This finally deletes the session from the UI
          }
          : conn
      );
    });

    toast.success(`Cable removed. Connector ${connectorId} is now Available.`);
  };


  // ✅ Safe way to handle the ocppClient
  // useEffect(() => {
  //   if (!ocppClient) return;

  //   const liveUpdateInterval = setInterval(() => {
  //     connectors.forEach(conn => {
  //       if (conn.status === ConnectorStatus.OCCUPIED && conn.activeSession) {
  //         // Now TypeScript knows ocppClient is NOT null here
  //         ocppClient.sendTransactionEvent(
  //           "Updated",
  //           conn.activeSession.transactionId,
  //           conn.id,
  //           undefined,
  //           undefined,
  //           conn.currentMeterReading
  //         );
  //       }
  //     });
  //   }, 5000);

  //   return () => clearInterval(liveUpdateInterval);
  // }, [ocppClient, connectors]);

 useEffect(() => {
  if (!ocppClient || !isConnected) return;

  const meterValuesInterval = 60000;

  const masterTicker = setInterval(() => {
    setConnectors(current => {
      current.forEach(conn => {
        if (conn.status === ConnectorStatus.OCCUPIED && conn.activeSession && conn.currentPower > 0) {

          // conn.currentMeterReading is read from inside setConnectors — always fresh
          const liveMeter = parseFloat(conn.currentMeterReading ?? '1000');

          console.log(`[OCPP METER TICK] Station: CP001, Connector: ${conn.id}, Value: ${liveMeter} Wh`);

          const meterValuePayload = [
            {
              timestamp: new Date().toISOString(),
              sampledValues: [
                {
                  value: liveMeter.toFixed(0),
                  context: "Sample.Periodic",
                  measurand: "Energy.Active.Import.Register",
                  unit: "Wh"
                },
                {
                  value: conn.currentPower.toFixed(0),
                  context: "Sample.Periodic",
                  measurand: "Power.Active.Import",
                  unit: "W"
                }
              ]
            }
          ];

          const messageId = ocppClient.sendMeterValues(conn.id, meterValuePayload);
          addOutgoingMessage('MeterValues', {
            evseId: conn.id,
            meterValue: meterValuePayload
          }, messageId);
        }
      });
      return current;
    });
  }, meterValuesInterval);

  return () => clearInterval(masterTicker);

  // ✅ simulatedData.energyActiveImportRegister REMOVED from deps
  // It was causing the interval to reset every 500ms, so 60s never elapsed
}, [ocppClient, isConnected, stationId, addOutgoingMessage]);

  useEffect(() => {
    const interval = setInterval(() => {
      setConnectors(current => current.map(conn => {
        if (conn.status === ConnectorStatus.OCCUPIED && conn.activeSession) {

          if (conn.currentPower <= 0) {
            return conn;
          }

          // 1. Calculate new battery percentage
          const newPercentage = Math.min(100, conn.chargePercentage + 0.05);

          // --- AUTO-STOP TRIGGER ---
          // If the new percentage hits 100, trigger the stop sequence
          if (newPercentage >= 100 && conn.chargePercentage < 100) {
            // We use setTimeout to call stopCharging outside the state update 
            // to avoid React rendering warnings
            setTimeout(() => {
              toast.success(`Connector ${conn.id}: Battery Full! Auto-stopping...`);
              stopCharging(conn.id);
            }, 0);
          }

          // 2. SESSION ENERGY: Delta between Master Meter and Session Start Meter
          const totalDeliveredWh = simulatedData.energyActiveImportRegister - conn.activeSession.energyConsumed;
          const energyInKwh = totalDeliveredWh / 1000;

          return {
            ...conn,
            chargePercentage: parseFloat(newPercentage.toFixed(1)),
            currentPower: conn.id === 1 ? 22000 : 50000,
            current: conn.id === 1 ? 32 : 64,
            currentMeterReading: simulatedData.energyActiveImportRegister.toFixed(0),
            activeSession: {
              ...conn.activeSession,
              sessionEnergyKwh: energyInKwh.toFixed(2)
            }
          };
        }
        return conn;
      }));

      // 3. MASTER METER UPDATE (Station Odometer)
      setSimulatedData(prev => {
        const activeChargingConnectors = connectors.filter(
          c => c.status === ConnectorStatus.OCCUPIED && c.currentPower > 0
        );

        const totalPower = activeChargingConnectors.reduce(
          (sum, conn) => sum + (conn.id === 1 ? 22000 : 50000), 0
        );
        const powerStep = (totalPower * 0.5) / 3600;
        return {
          ...prev,
          energyActiveImportRegister: prev.energyActiveImportRegister + powerStep,
          powerActiveImport: totalPower,
          currentImport: activeChargingConnectors.reduce((sum, conn) => sum + (conn.id === 1 ? 32 : 64), 0),
          voltage: 230 + (Math.random() * 5)
        };
      });
    }, 500);

    return () => clearInterval(interval);
  }, [connectors, simulatedData.energyActiveImportRegister, stopCharging]); // Add stopCharging to dependency array

  const sendMeterValues = (connectorId: number) => {
    const connector = connectors.find(c => c.id === connectorId);
    if (!connector?.activeSession) {
      toast.error('No active session for meter values');
      return;
    }

    if (ocppClient) {
      const meterValue = {
        timestamp: new Date().toISOString(),
        sampledValues: [
          {
            value: connector.currentPower.toFixed(0),
            context: 'Sample.Periodic',
            measurand: 'Power.Active.Import',
            location: 'Outlet',
            unit: 'W'
          },
          {
            value: connector.current.toFixed(1),
            context: 'Sample.Periodic',
            measurand: 'Current.Import',
            location: 'Outlet',
            unit: 'A'
          },
          {
            value: simulatedData.voltage.toFixed(1),
            context: 'Sample.Periodic',
            measurand: 'Voltage',
            location: 'Outlet',
            unit: 'V'
          },
          {
            value: simulatedData.energyActiveImportRegister.toFixed(0),
            context: 'Sample.Periodic',
            measurand: 'Energy.Active.Import.Register',
            location: 'Outlet',
            unit: 'Wh'
          }
        ]
      };

      const meterMessageId = ocppClient.sendMeterValues(connectorId, meterValue);
      addOutgoingMessage('MeterValues', {
        evseId: connectorId,
        meterValue: [meterValue]
      }, meterMessageId);
      toast.success('Meter values sent');
    }
  };


  useEffect(() => {
    const handleIncoming = (event: any) => {
      const { action, payload, messageId } = event.detail;
      if (!ocppClient) return;

      console.log("📥 UI RECEIVED:", action, payload);

      // 1. Handle Backend Acceptance/Rejection
      if (action === "BootNotificationResponse") {
        if (payload.status === "Accepted") {
          setBackendStatus('ACCEPTED');
          toast.success("✅ Station Verified: Online");
        } else {
          setBackendStatus('REJECTED');
          toast.error("🛑 SECURITY ALERT: Price Book Expired.");
        }
        return;
      }

      // 2. UNBLOCK Configuration Requests
      if (action === "GetConfiguration") {
        ocppClient.sendCallResult(messageId, {
          configurationKey: [{ key: "NumberOfConnectors", readonly: true, value: "2" }]
        });
        return;
      }

      // 3. Handle ON-DEMAND Meter Requests
      if (action === "DataTransfer") {
        const { vendorId, messageId: subAction } = payload;
        if (vendorId === "com.evcharge.debug" && subAction === "GetLiveMeter") {
          ocppClient.sendCallResult(messageId, {
            status: "Accepted",
            data: simulatedData.energyActiveImportRegister.toString()
          });
          return;
        }
      }

      // 4. Handle Mobile Start Requests
      if (action === "RemoteStartTransaction") {
        if (backendStatus === 'REJECTED') {
          ocppClient.sendCallResult(messageId, { status: "Rejected" });
          toast.error("Blocked: Station rejected by backend.");
          return;
        }

        ocppClient.sendCallResult(messageId, { status: "Accepted" });
        const targetId = payload.connectorId;
        const idTag = payload.idTag;
        const transactionId = payload.transactionId;

        setConnectors(current => {
          const connector = current.find(c => c.id === targetId);
          if (connector) {
            if (connector.status === ConnectorStatus.AVAILABLE) plugInCar(targetId);
            setTimeout(() => startCharging(targetId, idTag, transactionId), 500);
          }
          return current;
        });
      }

      // 5. Handle Mobile Stop Requests
      if (action === "RemoteStopTransaction") {
        ocppClient.sendCallResult(messageId, { status: "Accepted" });
        const stopTxId = payload.transactionId;

        setConnectors(current => current.map(conn => {
          if (conn.activeSession?.transactionId === stopTxId) {
            toast.success(`Remote Stop Successful`);
            ocppClient.sendTransactionEvent('Ended', stopTxId, conn.id, 'Remote', undefined, simulatedData.energyActiveImportRegister);
            return { ...conn, status: ConnectorStatus.OCCUPIED, currentPower: 0, current: 0 };
          }
          return conn;
        }));
      }

      // Handle Admin changing station Online/Offline
      // Handle Admin changing station Online/Offline
      if (action === "ChangeAvailability") {
        console.log("✅ Simulator caught ChangeAvailability! Replying to Java...");
        
        // 1. Reply to Java immediately so it doesn't time out!
        ocppClient.sendCallResult(messageId, { status: "Accepted" });
        
        // 2. Update the React UI State
        if (payload.type === "Inoperative") {
          toast.error("🔌 Station has been taken OFFLINE by Admin.");
          
          // Change the top dot to Red
          setBackendStatus('REJECTED'); 
          
          // Lock all connectors so the UI turns red and disables buttons
          setConnectors(current => current.map(conn => ({
            ...conn,
            status: ConnectorStatus.UNAVAILABLE 
          })));
          
        } else {
          toast.success("⚡ Station is back ONLINE.");
          
          // Change the top dot back to Green
          setBackendStatus('ACCEPTED');
          
          // Unlock the connectors back to Available
          setConnectors(current => current.map(conn => ({
            ...conn,
            status: ConnectorStatus.AVAILABLE
          })));
        }
        return;
      }

      // Handle Admin Rebooting the station
      if (action === "Reset") {
        // 1. Reply to Java immediately
        ocppClient.sendCallResult(messageId, { status: "Accepted" });
        toast.error("🔄 Station Rebooting...");
        
        // 2. Simulate dropping the connection and reconnecting
        setTimeout(() => {
          disconnect();
          setTimeout(() => {
            connect();
            toast.success("✨ Station back online!");
          }, 3000);
        }, 1000);
        return;
      }
    };

    window.addEventListener('ocpp-message-incoming', handleIncoming);

    // ⚡ NEW: Backfill Check
    // If we are already connected but state is UNKNOWN, check the messages array for a Boot response
    if (isConnected && backendStatus === 'UNKNOWN') {
      const bootMsg = messages.find(m => m.action === "BootNotificationResponse");
      if (bootMsg && bootMsg.payload?.status === "Accepted") {
        setBackendStatus('ACCEPTED');
      }
    }

    return () => window.removeEventListener('ocpp-message-incoming', handleIncoming);

    // Ensure all state dependencies are here
  }, [ocppClient, isConnected, messages, plugInCar, startCharging, stopCharging, simulatedData.energyActiveImportRegister, backendStatus]); // ^ Added simulatedData to dependencies so the handler has the fresh meter value
// Ref so syncStateWithBackend can read the live meter without being a dependency
const simulatedDataRef = React.useRef(simulatedData);
useEffect(() => {
  simulatedDataRef.current = simulatedData;
}, [simulatedData]);
  // Inside ChargingStationSimulator component
  useEffect(() => {
  if (!isConnected || !stationId) return;

  const syncStateWithBackend = async () => {
    try {
      const response = await fetch(`https://charge99.net/ccms/api/sessions/station/${stationId}`);
      if (!response.ok) return;

      const sessions = await response.json();

      setConnectors(prevConnectors => prevConnectors.map(conn => {
        const activeSession = sessions.find((s: any) =>
          s.connectorId === conn.id &&
          (s.status === 'CHARGING' || s.status === 'STARTING' || s.status === 'STARTED')
        );

        if (activeSession) {
          // Only sync if we don't already have this session tracked locally.
          // NEVER overwrite an already-running local session — it would break
          // the energyConsumed delta calculation.
          if (!conn.activeSession || conn.activeSession.transactionId !== activeSession.transactionId) {
            console.log(`[SYNC] Restoring lost session for Connector ${conn.id}`);
            return {
              ...conn,
              status: ConnectorStatus.OCCUPIED,
              currentPower: conn.id === 1 ? 22000 : 50000,
              current: conn.id === 1 ? 32 : 64,
              activeSession: {
                transactionId: activeSession.transactionId,
                idTag: activeSession.idTag,
                startTime: new Date(activeSession.startTime),
                // Anchor the delta to the backend meterStart so kWh calc is correct
                energyConsumed: activeSession.meterStart ?? simulatedDataRef.current.energyActiveImportRegister,
                sessionEnergyKwh: activeSession.energyConsumed?.toFixed(2) ?? "0.00"
              }
            };
          }
          // Session already tracked locally — don't touch it
          return conn;
        }

        // No active session on backend. Only reset if we have NO active charging
        // power (i.e. stopCharging has already fired locally). This prevents the
        // sync from wiping a connector that just started and hasn't reached Java yet.
        if ((conn.status === ConnectorStatus.OCCUPIED || conn.activeSession) && conn.currentPower <= 0) {
          console.log(`[SYNC] Backend confirms session ended for Connector ${conn.id}, holding OCCUPIED for unplug.`);
          return {
            ...conn,
            status: ConnectorStatus.OCCUPIED,
            currentPower: 0,
            current: 0
          };
        }

        return conn;
      }));
    } catch (err) {
      console.error("State sync failed:", err);
    }
  };

  syncStateWithBackend();
  const syncInterval = setInterval(syncStateWithBackend, 5000);
  return () => clearInterval(syncInterval);
}, [isConnected, stationId]); // intentionally NOT depending on simulatedData

  return (
    <div className="space-y-6">
      {/* Station Configuration & OCPP Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Station Configuration */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Station Configuration</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Station ID</label>
                <input
                  type="text"
                  value={stationId}
                  onChange={(e) => setStationId(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  disabled={isConnected}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Station Name</label>
                <input
                  type="text"
                  value={stationName}
                  onChange={(e) => setStationName(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  {!isConnected ? (
                    <button
                      onClick={handleConnect} // ✅ Change from 'connect' to 'handleConnect'
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-success-600 hover:bg-success-700"
                    >
                      Connect
                    </button>
                  ) : (
                    <button
                      onClick={handleDisconnect} // ✅ Change to 'handleDisconnect'
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-danger-600 hover:bg-danger-700"
                    >
                      Disconnect
                    </button>
                  )}
                </div>
                <div className="flex items-center">
                  <div className={`h-3 w-3 rounded-full ${!isConnected ? 'bg-gray-400' : // Socket closed (Gray)
                    backendStatus === 'ACCEPTED' ? 'bg-success-400 animate-pulse' : // Approved (Green)
                      backendStatus === 'REJECTED' ? 'bg-red-500' : // Security Block (Red)
                        'bg-yellow-400 animate-bounce' // WebSocket open but waiting for Java (Yellow)   -
                    }`} />

                  <span className="ml-2 text-sm text-gray-600 font-medium">
                    {!isConnected ? 'Disconnected' :
                      backendStatus === 'ACCEPTED' ? 'Online (Accepted)' :
                        backendStatus === 'REJECTED' ? 'Access Denied' :
                          'Verifying Price Book...'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* OCPP Actions */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">OCPP Actions</h2>
            {isConnected ? (
              <div className="space-y-3">
                <button
                  onClick={handleSendBootNotification}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Send Boot Notification
                </button>
                <button
                  onClick={handleSendHeartbeat}
                  // ✅ Disable if not fully verified
                  disabled={!isFullyOperational}
                  className={`w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md ${!isFullyOperational ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                  Send Heartbeat
                </button>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-sm">
                  Connect to station to enable OCPP actions
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Connectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {connectors.map(connector => (
          <ConnectorPanel
            key={connector.id}
            connector={connector}
            disabled={backendStatus !== 'ACCEPTED'}
            simulatedData={simulatedData}
            isConnected={isConnected}
            onStatusChange={updateConnectorStatus}
            onStartCharging={startCharging}
            onStopCharging={stopCharging}
            onSendMeterValues={sendMeterValues}
            onPlugInCar={plugInCar}
            onUnplugCar={unplugCar}
          />
        ))}
      </div>

      {/* Sessions and Messages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SessionManager connectors={connectors} />
        <MessageLogger messages={messages} />
      </div>
    </div>
  );
};

export default ChargingStationSimulator;